import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiPlus, FiX, FiChevronLeft, FiTrash2, FiCheck, FiChevronRight } from 'react-icons/fi';

const API_URL = process.env.REACT_APP_API_URL || 'https://green-admin.merket.io/api';

function CategoryManagement() {
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '',
    order: 0,
    active: true
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Bulk select for mobile
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const longPressTimer = useRef(null);
  const isLongPress = useRef(false);

  useEffect(() => {
    loadCategories();
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/categories`);
      setCategories(response.data.data);
    } catch (error) {
      toast.error('Không thể tải danh mục');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await axios.put(`${API_URL}/categories/${editingCategory._id}`, formData);
      } else {
        await axios.post(`${API_URL}/categories`, formData);
      }
      loadCategories();
      closeModal();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Lỗi khi lưu danh mục');
    }
  };

  const deleteCategory = async (id) => {
    if (window.confirm('Xóa danh mục này? Lưu ý: Các món ăn trong danh mục sẽ không bị xóa.')) {
      try {
        await axios.delete(`${API_URL}/categories/${id}`);
        loadCategories();
      } catch (error) {
        toast.error('Không thể xóa danh mục');
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedCategories.length === 0) return;
    if (!window.confirm(`Xóa ${selectedCategories.length} danh mục đã chọn?`)) return;
    
    try {
      await Promise.all(selectedCategories.map(id => axios.delete(`${API_URL}/categories/${id}`)));
      setSelectedCategories([]);
      setBulkSelectMode(false);
      loadCategories();
    } catch (error) {
      toast.error('Không thể xóa danh mục');
    }
  };

  const openModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        description: category.description || '',
        icon: category.icon || '',
        order: category.order || 0,
        active: category.active !== undefined ? category.active : true
      });
    } else {
      setEditingCategory(null);
      setFormData({ name: '', description: '', icon: '', order: 0, active: true });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCategory(null);
  };

  // Long press handlers
  const handleTouchStart = (e, categoryId) => {
    isLongPress.current = false;
    longPressTimer.current = setTimeout(() => {
      isLongPress.current = true;
      setBulkSelectMode(true);
      toggleSelection(categoryId);
      if (navigator.vibrate) navigator.vibrate(50);
    }, 800);
  };

  const handleTouchEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleTouchMove = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const toggleSelection = (categoryId) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const exitBulkSelectMode = () => {
    setBulkSelectMode(false);
    setSelectedCategories([]);
  };

  // Mobile Category Card
  const MobileCategoryCard = ({ category }) => {
    const isSelected = selectedCategories.includes(category._id);
    
    return (
      <div 
        className={`bg-white flex items-center p-4 active:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}
        onTouchStart={(e) => handleTouchStart(e, category._id)}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
        onClick={() => {
          if (isLongPress.current) return;
          if (bulkSelectMode) {
            toggleSelection(category._id);
          } else {
            openModal(category);
          }
        }}
      >
        {/* Checkbox for bulk select */}
        {bulkSelectMode && (
          <div className="mr-3">
            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? 'bg-green-500 border-green-500' : 'border-gray-300 bg-white'}`}>
              {isSelected && <FiCheck className="text-white" size={14} />}
            </div>
          </div>
        )}
        
        {/* Icon */}
        {category.icon && (
          <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3 text-xl">
            {category.icon}
          </div>
        )}
        
        {/* Name */}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900">{category.name}</p>
          {!category.active && (
            <span className="text-xs text-gray-500">Đã ẩn</span>
          )}
        </div>
        
        {/* Arrow */}
        {!bulkSelectMode && <FiChevronRight className="text-gray-300" size={20} />}
      </div>
    );
  };

  return (
    <div className={isMobile ? "-m-4 bg-gray-50 pb-4" : "space-y-6"}>
      {/* Header */}
      {isMobile ? (
        bulkSelectMode ? (
          // Bulk select header
          <div className="bg-green-500 text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button onClick={exitBulkSelectMode} className="p-1">
                <FiX size={20} />
              </button>
              <span className="font-medium">{selectedCategories.length} đã chọn</span>
            </div>
            <button
              onClick={handleBulkDelete}
              disabled={selectedCategories.length === 0}
              className="p-2 rounded-full bg-white/20 disabled:opacity-50"
            >
              <FiTrash2 size={18} />
            </button>
          </div>
        ) : (
          // Normal header
          <div className="bg-white px-4 py-3 flex items-center justify-between shadow-sm">
            <h1 className="text-lg font-bold text-gray-800">Quản lý danh mục</h1>
            <button
              onClick={() => openModal()}
              className="flex items-center space-x-1 px-3 py-2 bg-green-600 text-white rounded-lg text-sm"
            >
              <FiPlus size={16} />
              <span>Thêm danh mục</span>
            </button>
          </div>
        )
      ) : (
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Quản lý danh mục</h1>
          <button
            onClick={() => openModal()}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <FiPlus />
            <span>Thêm danh mục</span>
          </button>
        </div>
      )}

      {/* Long press hint - Mobile */}
      {isMobile && !bulkSelectMode && categories.length > 0 && (
        <div className="px-4 py-2 text-xs text-gray-500 text-center">
          Giữ lâu để chọn nhiều danh mục
        </div>
      )}

      {/* Categories List */}
      {isMobile ? (
        <div className="divide-y divide-gray-100">
          {categories.map((category) => (
            <MobileCategoryCard key={category._id} category={category} />
          ))}
          {categories.length === 0 && (
            <div className="text-center py-12 text-gray-500 text-sm">
              Chưa có danh mục nào
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tên danh mục</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mô tả</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Icon</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Thứ tự</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trạng thái</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Thao tác</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {categories.map((category) => (
                <tr key={category._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold">{category.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{category.description || '-'}</td>
                  <td className="px-6 py-4 text-2xl">{category.icon || '-'}</td>
                  <td className="px-6 py-4">{category.order}</td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      category.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {category.active ? 'Hoạt động' : 'Ẩn'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right space-x-2">
                    <button
                      onClick={() => openModal(category)}
                      className="text-blue-600 hover:text-blue-800 px-2 py-1"
                    >
                      Sửa
                    </button>
                    <button
                      onClick={() => deleteCategory(category._id)}
                      className="text-red-600 hover:text-red-800 px-2 py-1"
                    >
                      Xóa
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className={`fixed inset-0 bg-black bg-opacity-50 z-[100] ${isMobile ? '' : 'flex items-center justify-center p-4'}`}>
          <div className={`bg-white ${isMobile ? 'fixed inset-0 overflow-y-auto' : 'rounded-lg max-w-md w-full p-6'}`}>
            {/* Modal Header */}
            <div className={`flex justify-between items-center sticky top-0 bg-white z-10 ${isMobile ? 'px-4 py-3 border-b shadow-sm' : 'mb-4'}`}>
              {isMobile && (
                <button onClick={closeModal} className="p-1 -ml-1">
                  <FiChevronLeft size={24} />
                </button>
              )}
              <h2 className={`font-bold ${isMobile ? 'text-lg flex-1 text-center' : 'text-xl'}`}>
                {editingCategory ? 'Sửa danh mục' : 'Thêm danh mục mới'}
              </h2>
              {isMobile ? (
                <button 
                  type="button"
                  onClick={() => document.getElementById('category-form').requestSubmit()}
                  className="text-green-600 font-semibold text-sm"
                >
                  Lưu
                </button>
              ) : (
                <button onClick={closeModal}>
                  <FiX size={24} />
                </button>
              )}
            </div>
            
            <form id="category-form" onSubmit={handleSubmit} className={`space-y-4 ${isMobile ? 'p-4 pb-24' : ''}`}>
              <div>
                <label className={`block font-semibold ${isMobile ? 'text-sm mb-1' : 'mb-2'}`}>Tên danh mục *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`w-full border rounded-lg ${isMobile ? 'px-3 py-2 text-sm' : 'px-4 py-2'}`}
                  placeholder="VD: Cafe, Trà sữa..."
                  required
                />
              </div>

              {/* Icon & Order - Side by side on mobile */}
              <div className={isMobile ? "grid grid-cols-2 gap-3" : ""}>
                <div className={!isMobile ? "mb-4" : ""}>
                  <label className={`block font-semibold ${isMobile ? 'text-sm mb-1' : 'mb-2'}`}>Icon (emoji)</label>
                  <input
                    type="text"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    className={`w-full border rounded-lg ${isMobile ? 'px-3 py-2 text-sm' : 'px-4 py-2'}`}
                    placeholder="☕ 🍕 🍔"
                  />
                </div>

                <div>
                  <label className={`block font-semibold ${isMobile ? 'text-sm mb-1' : 'mb-2'}`}>Thứ tự</label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    className={`w-full border rounded-lg ${isMobile ? 'px-3 py-2 text-sm' : 'px-4 py-2'}`}
                  />
                </div>
              </div>

              <div>
                <label className={`block font-semibold ${isMobile ? 'text-sm mb-1' : 'mb-2'}`}>Mô tả</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className={`w-full border rounded-lg ${isMobile ? 'px-3 py-2 text-sm' : 'px-4 py-2'}`}
                  rows={isMobile ? 2 : 3}
                  placeholder="Mô tả ngắn về danh mục..."
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  id="active-check"
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="active-check" className={isMobile ? 'text-sm' : ''}>Hiển thị danh mục</label>
              </div>

              {/* Delete button - Mobile only, edit mode only */}
              {isMobile && editingCategory && (
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm('Xóa danh mục này?')) {
                      deleteCategory(editingCategory._id);
                      closeModal();
                    }
                  }}
                  className="w-full py-2.5 text-red-600 font-medium border border-red-200 rounded-lg mt-4"
                >
                  Xóa danh mục này
                </button>
              )}

              {/* Desktop submit button */}
              {!isMobile && (
                <button
                  type="submit"
                  className="w-full bg-green-600 text-white py-3 rounded-lg font-bold hover:bg-green-700"
                >
                  {editingCategory ? 'Cập nhật' : 'Thêm danh mục'}
                </button>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CategoryManagement;
